import React, { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../hooks/useAuth";
import {
  useUsersWithModuleAccess,
  ModuleAccessUser,
} from "../hooks/useUsersWithModuleAccess";
import {
  downloadQuotePdf,
  QUOTE_PAGE_WIDTH_PX,
  QuoteDocData,
} from "./smrtChequesQuoteDoc";
import { loadPdfjs } from "../utils/pdfWorker";
import "../styles/smrtcheques.css";

// Brook logo path resolved from public/. Drop the file at
// askSmart-ui/public/brook-logo.png for it to appear in the PDF header.
const BROOK_LOGO_URL = `${process.env.PUBLIC_URL || ""}/brook-logo.png`;

// SMRT Tasks bridge — same endpoints SMRT Tasks itself consumes. We're a
// client of that module, not modifying it.
const TASKS_API_URL = `${process.env.REACT_APP_BACKEND_URL}/smrt-tasks`;
const TASK_TYPES_API_URL = `${process.env.REACT_APP_BACKEND_URL}/smrt-task-types`;

interface CreateChequeTaskParams {
  title: string;
  description: string;
  assigneeId: string;
  chequeId: string;
}

// Stable, parseable marker we embed in every CRMTIX task description so
// the resolve effect can join tasks back to cheques by exact id rather
// than by title-regex parsing of projectRef. Survives any user-entered
// projectRef format.
const CHEQUE_ID_TOKEN_PREFIX = "[cheque:";
const CHEQUE_ID_TOKEN_RE = /\[cheque:([^\]]+)\]/;
function extractChequeIdFromDescription(
  description?: string,
): string | undefined {
  if (!description) return undefined;
  const m = description.match(CHEQUE_ID_TOKEN_RE);
  return m?.[1];
}

// POSTs a cheque-driven task into SMRT Tasks. Title and description are
// supplied by the caller — the join key back to the cheque is encoded into
// the title (project ref or PO number, parsed by extractRefsFromTitle).
// Failures are logged but swallowed: a transient task-create hiccup must
// never block the cheque/PO persistence flow.
async function createChequeTask(
  params: CreateChequeTaskParams,
): Promise<void> {
  const { title, description, assigneeId, chequeId } = params;
  if (!assigneeId) {
    console.warn("[SMRTCheques] skipping task — no assignee provided");
    return;
  }
  // Embed the cheque id at the top of the description with a stable token.
  // The resolve effect uses this for the chequeId join — robust against any
  // user-entered projectRef format that the title regex might miss.
  const taggedDescription =
    `${CHEQUE_ID_TOKEN_PREFIX}${chequeId}]\n\n${description}`;
  try {
    const typesRes = await fetch(TASK_TYPES_API_URL, {
      credentials: "include",
    });
    if (!typesRes.ok) throw new Error(`task types ${typesRes.status}`);
    const typesData = await typesRes.json();
    const types: Array<{ id: string; name: string }> = Array.isArray(
      typesData?.taskTypes,
    )
      ? typesData.taskTypes
      : [];
    if (types.length === 0) {
      console.warn(
        "[SMRTCheques] skipping task — no task types defined for org. " +
          "Add one in Manage SMRT Tasks (a 'Cheques' type is preferred).",
      );
      return;
    }
    // Prefer a "Cheque*" type so cheque-driven tasks bucket cleanly in the
    // SMRT Tasks UI; otherwise fall back to the first available type.
    const preferred =
      types.find((t) => /cheque/i.test(t.name)) || types[0];

    const res = await fetch(TASKS_API_URL, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        title,
        description: taggedDescription,
        taskTypeId: preferred.id,
        assignedUserIds: [assigneeId],
        source: "crmtix",
      }),
    });
    if (!res.ok) throw new Error(`task POST ${res.status}`);
  } catch (err) {
    console.error("[SMRTCheques] failed to create task:", err);
  }
}

// Route this page is wired to in App.js — also the canonical identifier
// the RBAC hook uses to locate this module's record in the registry.
const SMRT_CHEQUES_ROUTE = "/smrt-crmtix";

// Admin permission key — kept in sync with ADMIN_PERMISSION in
// askSmart-backend/src/routes/smrtCheques.ts. Holders bypass per-step
// assignee gating on action buttons (Issue PO, Upload Cheque & Shipment).
const ADMIN_PERMISSION = "sMRTCRMTIX.admin";

// ─── Cheque-bound task classification ────────────────────────────────────────
// Tasks created by this module embed `Cheque ID: <id>` in their description so
// we can join them back to a cheque, but we still need to know which workflow
// step a task represents. Title patterns are stable enough to classify on —
// they're written by createChequeTask() right here in this file.
type ChequeTaskKind = "po" | "gr" | "prepare" | "sent" | "cashed";
type ChequeTaskStatus = Partial<Record<ChequeTaskKind, string>>;

// Stage-1 task representation: classified by title but not yet joined to a
// specific cheque. Resolution happens in a downstream effect that has the
// cheques snapshot in scope.
interface RawChequeTask {
  kind: ChequeTaskKind;
  status: string;
  // Cheque id parsed from the description marker. New CRMTIX tasks have it;
  // legacy tasks (created before the marker was added) fall back to the
  // projectRef/poNumber matchers below.
  chequeId?: string;
  projectRef?: string;
  poNumber?: string;
  // Auth0 sub of every user assigned to this task. Carried through so the
  // CRMTIX action-button gates can grant access to the user who actually
  // performed the task — defending against drift between the cheque's
  // separately-stored `assignments` field and the live SMRT Task assignees.
  assigneeIds: string[];
}

function classifyChequeTask(title?: string): ChequeTaskKind | null {
  if (!title) return null;
  // Order matters: "PO Receipt" must beat any other PO match; classifier
  // is title-only, so each pattern must be unique against the others.
  if (/^PO\s+Receipt/i.test(title)) return "po";
  if (/^GR\s+Status\s+for\s+PO/i.test(title)) return "gr";
  if (/confirmation\s+to\s+Client/i.test(title)) return "sent";
  if (/^Prepare\s+Cheque/i.test(title)) return "prepare";
  // Permissive: the project ref between "Cheque" and "Cashed" can be
  // multi-word when the upload's filename fallback fires, so don't require
  // a single \S+ token — that broke classification for multi-word refs.
  if (/^Cheque\b.*\bCashed\b/i.test(title)) return "cashed";
  return null;
}

// Pulls the cheque-resolution tokens out of a task title. Tasks reference
// either a project ref (P-XXXXX, used by PO/Prepare/Send/Cashed) or a PO
// number (used only by GR — its title is "GR Status for PO <num>"). The
// resolve effect uses whichever lands to find the matching cheque.
function extractRefsFromTitle(
  title: string,
): { projectRef?: string; poNumber?: string } {
  const projectMatch = title.match(/\b(P-\d{4,})\b/);
  // Require a digit at the start of the PO-number capture so "PO Receipt"
  // doesn't get parsed as PO number "Receipt".
  const poMatch = title.match(/\bPO\s+(\d\S*)/i);
  return {
    projectRef: projectMatch?.[1],
    poNumber: poMatch?.[1],
  };
}

// ─── Types ────────────────────────────────────────────────────────────────────

type WorkflowStepKey =
  | "quote_uploaded"
  | "po_received"
  | "gr_checked"
  | "cheque_prepared"
  | "cheque_sent"
  | "cheque_cashed";

interface WorkflowStep {
  key: WorkflowStepKey;
  label: string;
  // Role label shown in the assignee column. "Ericsson" for the inbound PO step
  // is informational — it's an external trigger, not a person we assign.
  roleLabel: string;
}

const WORKFLOW: WorkflowStep[] = [
  { key: "quote_uploaded",  label: "Quote uploaded to client",                       roleLabel: "Quote Owner" },
  { key: "po_received",     label: "PO received and recorded",                       roleLabel: "Quote Owner" },
  { key: "gr_checked",      label: "GR checked & QBO updated",                       roleLabel: "Finance" },
  // cheque_prepared is the only step in the workflow that is NOT task-
  // driven. Its timestamp is set directly by submitUpload when both the
  // cheque photo and the UPS envelope photo have been provided.
  { key: "cheque_prepared", label: "Cheque & shipping photos captured, cheque sent", roleLabel: "Cheque Preparer" },
  { key: "cheque_sent",     label: "Photos sent to client",                          roleLabel: "Quote Owner" },
  { key: "cheque_cashed",   label: "Confirmed cheque cashed",                        roleLabel: "Finance" },
];

// Org user shape used by the assignment dropdowns. id = Auth0 user_id.
// Populated dynamically from /approved_users + /roles, filtered to users
interface ChequeAssignments {
  // Maps step.key → user id. po_received has no assignee (external).
  quote_uploaded?: string;
  gr_checked?: string;
  cheque_prepared?: string;
  cheque_sent?: string;
  cheque_cashed?: string;
}

interface ChequeTimeline {
  quote_uploaded?: string;
  po_received?: string;
  gr_checked?: string;
  cheque_prepared?: string;
  cheque_sent?: string;
  cheque_cashed?: string;
}

interface ChequeRecord {
  id: string;
  // Source data (from Ericsson input doc — read-only on the form).
  source: {
    projectRef: string;        // P-439295
    siteId: string;            // Kelleys Island-OHA38
    payable: string;           // Diamond Communications
    chequeAmount: number;      // 1500.00
    deliveryType: string;      // Overnight
    mailTo: string;            // Diamond Communications, LLC
    attn: string;              // Keith Goldberg
    address: string;           // 120 Mountain Avenue
    city: string;              // Springfield
    state: string;             // NJ
    zip: string;               // 07081
    memo: string;              // Diamond Site ID #: Kelleys Island-OHA38
    ericssonContactEmail: string;  // shelly.montalvo@ericsson.com
    reasonForCheck: string;    // Closeout Package
  };
  // Quote builder fields (editable, persisted into the generated quote).
  quote: {
    projectRef: string;
    siteName: string;
    vendor: string;
    scope: string;
    baseAmount: number;
    shippingFee: number;
    markupPct: number; // default 5.35
    total: number;     // derived but cached
  };
  assignments: ChequeAssignments;
  timeline: ChequeTimeline;
  // Latest workflow step that has a timestamp — drives the status pill.
  status: WorkflowStepKey | "draft";
  attachmentCount: number;
  // PO data captured manually after the Ericsson PO arrives. Only populated
  // once the user clicks "Issue" and submits the modal.
  po?: {
    number: string;
    fileName?: string;
    fileDataUrl?: string; // base64 data URL, persisted alongside the record
  };
  // Cheque-shipment evidence captured via the "Upload Cheque & Shipment"
  // modal that appears once the GR task is complete. Two optional photos
  // (cheque image + UPS envelope) stored as base64 data URLs, same pattern
  // as `po.fileDataUrl`.
  shipment?: {
    chequeFileName?: string;
    chequeDataUrl?: string;
    upsFileName?: string;
    upsDataUrl?: string;
  };
  // Server-assigned identity fields. Populated once the doc lands in
  // SMRTCheques (POST response or list fetch); absent on local-only drafts.
  orgID?: string;
  createdBy?: string;
  createdAt?: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const MARKUP_PCT_DEFAULT = 5.35;
const SHIPPING_FEE_DEFAULT = 95;

// Markup is applied to (base + shipping), not base alone.
// total = (base + shipping) * (1 + markupPct / 100).
function computeTotal(base: number, shipping: number, markupPct: number): number {
  const safeBase = Number.isFinite(base) ? base : 0;
  const safeShip = Number.isFinite(shipping) ? shipping : 0;
  const safeMk = Number.isFinite(markupPct) ? markupPct : 0;
  const subtotal = safeBase + safeShip;
  return Math.round((subtotal + (subtotal * safeMk) / 100) * 100) / 100;
}

function fmtCurrency(n: number): string {
  return n.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
  });
}

function fmtDateTime(iso?: string): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

// "Ready to generate" gate: every quote field carries a value AND every
// non-auto workflow step has an assignee picked. Drives the disabled state
// of the Generate Quote button so the user can't fire off an incomplete doc.
function isQuoteReady(draft: ChequeRecord): boolean {
  const q = draft.quote;
  const a = draft.assignments;
  const fieldsOK =
    !!q.projectRef.trim() &&
    !!q.siteName.trim() &&
    !!q.vendor.trim() &&
    !!q.scope.trim() &&
    q.baseAmount > 0;
  const assignmentsOK =
    !!a.quote_uploaded &&
    !!a.gr_checked &&
    !!a.cheque_prepared &&
    !!a.cheque_sent &&
    !!a.cheque_cashed;
  return fieldsOK && assignmentsOK;
}

function statusPill(status: ChequeRecord["status"]): {
  label: string;
  className: string;
} {
  switch (status) {
    case "draft":
      return { label: "Draft", className: "smrtcheques-pill-draft" };
    case "quote_uploaded":
      return { label: "Awaiting PO", className: "smrtcheques-pill-amber" };
    case "po_received":
      return { label: "PO Received", className: "smrtcheques-pill-blue" };
    case "gr_checked":
      return { label: "GR Checked", className: "smrtcheques-pill-blue" };
    case "cheque_prepared":
      return { label: "Cheque Prepared", className: "smrtcheques-pill-blue" };
    case "cheque_sent":
      return { label: "Cheque Sent", className: "smrtcheques-pill-blue" };
    case "cheque_cashed":
      return { label: "Cashed", className: "smrtcheques-pill-green" };
  }
}

// ─── Backend persistence ─────────────────────────────────────────────────────
// Cheques are persisted in the SMRTCheques Cosmos container (POST/GET/PUT/
// DELETE on /smrt-cheques). The id field is server-generated; the client
// strips its draft id before POSTing and adopts the returned doc verbatim.
// All mutations refresh local state from the response — no optimistic-then-
// reconcile dance, since cheque volume is tiny and the round-trip is fast.

const CHEQUES_API_URL = `${process.env.REACT_APP_BACKEND_URL}/smrt-cheques`;

// Strips the client-side draft id so the backend assigns the canonical one.
// Everything else on the cheque is editable from the client's perspective.
function toCreatePayload(c: ChequeRecord): Omit<ChequeRecord, "id"> {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { id, ...rest } = c;
  return rest;
}

async function loadCheques(): Promise<ChequeRecord[]> {
  const res = await fetch(CHEQUES_API_URL, { credentials: "include" });
  if (!res.ok) throw new Error(`load cheques ${res.status}`);
  const data = await res.json();
  return Array.isArray(data?.cheques) ? (data.cheques as ChequeRecord[]) : [];
}

async function createCheque(c: ChequeRecord): Promise<ChequeRecord> {
  const res = await fetch(CHEQUES_API_URL, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(toCreatePayload(c)),
  });
  if (!res.ok) throw new Error(`create cheque ${res.status}`);
  return (await res.json()) as ChequeRecord;
}

async function updateCheque(
  c: ChequeRecord,
  orgID: string,
): Promise<ChequeRecord> {
  const res = await fetch(`${CHEQUES_API_URL}/${encodeURIComponent(c.id)}`, {
    method: "PUT",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ...toCreatePayload(c), orgID }),
  });
  if (!res.ok) throw new Error(`update cheque ${res.status}`);
  return (await res.json()) as ChequeRecord;
}

async function deleteCheque(id: string, orgID: string): Promise<void> {
  const url = `${CHEQUES_API_URL}/${encodeURIComponent(id)}?orgID=${encodeURIComponent(orgID)}`;
  const res = await fetch(url, { method: "DELETE", credentials: "include" });
  if (!res.ok) throw new Error(`delete cheque ${res.status}`);
}

// Empty source/quote pair for the New-Cheque scratch state.
function emptyDraft(): ChequeRecord {
  const id = `ch_${Date.now()}`;
  return {
    id,
    source: {
      projectRef: "",
      siteId: "",
      payable: "",
      chequeAmount: 0,
      deliveryType: "",
      mailTo: "",
      attn: "",
      address: "",
      city: "",
      state: "",
      zip: "",
      memo: "",
      ericssonContactEmail: "",
      reasonForCheck: "",
    },
    quote: {
      projectRef: "",
      siteName: "",
      vendor: "",
      scope: "",
      baseAmount: 0,
      shippingFee: SHIPPING_FEE_DEFAULT,
      markupPct: MARKUP_PCT_DEFAULT,
      total: 0,
    },
    assignments: {},
    timeline: {},
    status: "draft",
    attachmentCount: 0,
  };
}

// Reads the raw text out of a File. PDFs are binary so most of the result
// is gibberish, but uncompressed text streams (the case for the vast majority
// of digitally-generated cheque-request PDFs) survive a naive readAsText and
// can be regex-mined below. Plain-text uploads (.txt, .csv) round-trip fully.
function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () =>
      resolve(typeof reader.result === "string" ? reader.result : "");
    reader.onerror = () => reject(reader.error);
    reader.readAsText(file);
  });
}

// Lazy-imports pdf.js and returns the document text as newline-separated
// lines, reconstructed from positioned glyph fragments. The dynamic import
// keeps the ~600 KB pdf.js bundle out of the initial app load — webpack
// code-splits it into the "pdfjs" chunk, fetched only on the first PDF
// upload. Worker configuration (self-hosted, single owner) lives in
// utils/pdfWorker.
async function extractPdfText(file: File): Promise<string> {
  const pdfjs = await loadPdfjs();
  const buf = await file.arrayBuffer();
  const doc = await pdfjs.getDocument({ data: buf }).promise;

  const lines: string[] = [];
  for (let pageNum = 1; pageNum <= doc.numPages; pageNum++) {
    const page = await doc.getPage(pageNum);
    const content = await page.getTextContent();
    // Bucket fragments by y-coordinate (transform[5]) with a 2pt tolerance,
    // then sort each bucket left-to-right by x (transform[4]). This rebuilds
    // visual reading order from the (otherwise arbitrary) byte order pdf.js
    // returns.
    type Frag = { x: number; str: string };
    const buckets: Array<{ y: number; items: Frag[] }> = [];
    for (const item of content.items) {
      const str = item.str;
      if (!str) continue;
      const tx = item.transform || [];
      const x = tx[4] ?? 0;
      const y = tx[5] ?? 0;
      const existing = buckets.find((b) => Math.abs(b.y - y) < 2);
      if (existing) existing.items.push({ x, str });
      else buckets.push({ y, items: [{ x, str }] });
    }
    // PDF y-axis is inverted (origin at bottom-left), so higher y = top.
    buckets.sort((a, b) => b.y - a.y);
    for (const b of buckets) {
      b.items.sort((p, q) => p.x - q.x);
      const line = b.items
        .map((i) => i.str)
        .join(" ")
        .replace(/\s+/g, " ")
        .trim();
      if (line) lines.push(line);
    }
  }
  return lines.join("\n");
}

// Labels we know how to recognise. Each known label doubles as a stop-marker
// when capturing some other label's value, so multi-word values stay intact
// even when the document doesn't put a newline between fields.
const KNOWN_LABELS = [
  "Payable to",
  "Amount of Check",
  "Reason for Check",
  "Delivery Type",
  "Business Name",
  "Mail To",
  "ATTN",
  "Address",
  "City",
  "State",
  "Zip",
  "Check Memo",
  "Memo",
  "Site ID",
  "Project ID",
  "Project Reference",
];

function escapeRe(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

// Pulls the value that follows `label`. The label match is whitespace-flexible
// (so "Payable to" matches "Payable  to" or "Payable\nto") and case-
// insensitive. The value is captured non-greedily up to the next KNOWN_LABELS
// label-with-colon, or end-of-string — that way a value can span what was
// originally several visual lines without bleeding into the next field.
function findField(text: string, label: string): string {
  const labelRe = escapeRe(label).replace(/\s+/g, "\\s+");
  const stops = KNOWN_LABELS.filter((l) => l !== label)
    .map((l) => escapeRe(l).replace(/\s+/g, "\\s+"))
    .join("|");
  const re = new RegExp(
    // Optional bullet/dash prefix, label, optional colon, then the value.
    // Lookahead requires a known label followed by a colon, so values can
    // include label-words ("City of …") without being truncated.
    `[•·*\\-\\s]*${labelRe}\\s*:?\\s*(.+?)(?=\\s+(?:${stops})\\s*:|$)`,
    "i",
  );
  const m = text.match(re);
  if (!m) return "";
  return m[1]
    .trim()
    .replace(/^[:•·*\-\s]+/, "") // drop residual leading punctuation
    .replace(/[\s.,;]+$/, "")    // and trailing punctuation
    .trim();
}

// Builds a ChequeRecord["source"] purely from the uploaded file. Each field
// is either filled from a label-driven match in the document text or left
// blank — there are no hardcoded defaults anywhere in this function.
async function extractFromFile(
  file: File,
): Promise<{ source: ChequeRecord["source"]; warning?: string }> {
  const baseName = file.name.replace(/\.[^.]+$/, "");
  // PDFs go through pdf.js (lazy-loaded) so we get real, decompressed text
  // with reading-order reconstruction; .txt/.csv/etc. fall back to a plain
  // FileReader.readAsText. Either path produces the same downstream `text`
  // string the existing field-mapping logic already consumes.
  let text = "";
  let warning: string | undefined;
  if (file.type === "application/pdf" || /\.pdf$/i.test(file.name)) {
    try {
      text = await extractPdfText(file);
      // Diagnostic: confirm extraction actually flowed before any regex
      // tuning. Strip once real fields populate end-to-end.
      console.log(
        `[SMRTCheques] extracted ${text.length} chars from ${file.name}`,
        text.slice(0, 300),
      );
      if (!text.trim()) {
        warning =
          "Couldn't read any text from this PDF — it may be scanned/image-only. Fields below will be blank.";
      }
    } catch (err) {
      const message = (err as Error)?.message || "unknown error";
      console.error("[SMRTCheques] PDF extraction failed:", err);
      warning = `Couldn't extract text from this PDF (${message}). Fields below will be blank.`;
      text = "";
    }
  } else {
    try {
      text = await readFileAsText(file);
    } catch {
      text = "";
    }
  }

  // Normalise the text before label matching:
  //   1. Strip leading bullet glyphs (•, ·) — these otherwise sit between
  //      the previous value and the next label, breaking findField's
  //      "next-label-with-colon" lookahead.
  //   2. Drop parenthetical annotations between a label and its colon
  //      (e.g. "Delivery Type (Overnight / 2nd day / ...): Overnight" →
  //      "Delivery Type: Overnight"), so findField captures the value
  //      itself rather than the explanatory parens.
  //   3. Strip stray prefix chars ($, #, *) sitting immediately before a
  //      known label (e.g. "$ Amount of Check:" → "Amount of Check:").
  //      Without this, the *previous* field's lookahead can't anchor on the
  //      following label and the regex fails outright — leaving that
  //      previous field blank in the UI (the bug that hid "Payable to").
  // All pre-processing only — findField and KNOWN_LABELS are unchanged.
  const labelAlt = KNOWN_LABELS.map((l) =>
    l.replace(/[.*+?^${}()|[\]\\]/g, "\\$&").replace(/\s+/g, "\\s+"),
  ).join("|");
  text = text
    .replace(/[•·]\s*/g, "")
    .replace(/(\b[A-Z][\w ]+?)\s*\([^)]*\)\s*:/g, "$1:")
    .replace(
      new RegExp(`[$#*]\\s*(?=(?:${labelAlt})\\s*:)`, "gi"),
      "",
    );

  const payable = findField(text, "Payable to");
  const businessName = findField(text, "Business Name");
  const mailTo = businessName || findField(text, "Mail To");
  const reasonForCheck = findField(text, "Reason for Check");
  const deliveryType = findField(text, "Delivery Type");
  const attn = findField(text, "ATTN");
  const address = findField(text, "Address");
  const city = findField(text, "City");

  // Amount: the captured value may include a leading "$" or trailing
  // currency text — pull just the numeric portion. Whitespace inside the
  // amount is stripped first because pdf.js positioning sometimes emits
  // values like "$ 2 ,000" with literal spaces between the digits.
  const amountStr = findField(text, "Amount of Check").replace(/\s+/g, "");
  const amountMatch = amountStr.match(/([\d,]+(?:\.\d{1,2})?)/);
  const chequeAmount = amountMatch
    ? Number(amountMatch[1].replace(/,/g, ""))
    : 0;

  // State: typically "NJ" — accept either the 2-letter form or the full
  // captured string if the document spells it out.
  const stateStr = findField(text, "State");
  const state = stateStr.match(/\b[A-Z]{2}\b/)?.[0] || stateStr;

  // Zip: pull just the 5-digit (or ZIP+4) portion in case the captured value
  // includes adjacent text.
  const zipStr = findField(text, "Zip");
  const zip = zipStr.match(/\d{5}(?:-\d{4})?/)?.[0] || "";

  // Memo: the Ericsson form puts the site identifier inside the memo line as
  // "<Vendor> Site ID #: <SITE>". Lift that out so siteId can be populated.
  const memo = findField(text, "Check Memo") || findField(text, "Memo");
  const siteIdFromMemo = memo
    .match(/Site\s*ID\s*#?\s*:?\s*(.+)$/i)?.[1]
    ?.trim();
  // Newer Ericsson forms put "Site ID" on its own line rather than nested
  // inside the memo. Try a direct labelled lookup first; fall through to
  // the memo-embedded form, then the filename, when not present.
  const siteIdLabeled = findField(text, "Site ID");

  // Project reference: prefer a labelled match, then a bare "P-XXXXXX"
  // anywhere in the document, then the filename as the last-resort fallback.
  const projectRefLabeled =
    findField(text, "Project ID") || findField(text, "Project Reference");
  const projectRefBare = text.match(/\bP-\d{5,}\b/)?.[0];
  const projectRef = projectRefLabeled || projectRefBare || baseName;

  const ericssonContactEmail =
    text.match(/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/)?.[0] || "";

  return {
    source: {
      projectRef,
      siteId: siteIdLabeled || siteIdFromMemo || baseName,
      payable,
      chequeAmount: Number.isFinite(chequeAmount) ? chequeAmount : 0,
      deliveryType,
      mailTo,
      attn,
      address,
      city,
      state,
      zip,
      memo,
      ericssonContactEmail,
      reasonForCheck,
    },
    warning,
  };
}

// ─── Component ────────────────────────────────────────────────────────────────

type ViewMode = "tracker" | "new";

export default function SMRTCheques() {
  const { user, roles } = useAuth();
  // Action-button gating: a cheque-step action is only enabled for the user
  // assigned to that step, or for any user holding the module's admin
  // permission. Backend should mirror this for hard enforcement (UI gating
  // is cosmetic — a determined caller can still curl the PUT).
  // Backend /session returns the auth0 sub on `user.userID` (see
   // sessionRouter.ts), NOT on `user.sub`. Reading the wrong field is
   // why the assignee gate was always failing for non-admins.
   const currentUserId: string | undefined = user?.userID;
  const isAdmin: boolean = Array.isArray(roles?.permissions)
    ? roles.permissions.includes(ADMIN_PERMISSION)
    : false;
  const [cheques, setCheques] = useState<ChequeRecord[]>([]);

  // Initial fetch + window-focus refresh from the SMRTCheques backend.
  // Mirrors the same pattern useUsersWithModuleAccess uses so cheques created
  // (or modified) by another user surface here without a full app reload.
  useEffect(() => {
    let cancelled = false;
    const refresh = async () => {
      try {
        const list = await loadCheques();
        if (!cancelled) setCheques(list);
      } catch (err) {
        if (!cancelled) {
          console.error("[SMRTCheques] failed to load cheques:", err);
        }
      }
    };
    refresh();
    const onFocus = () => refresh();
    const onVisible = () => {
      if (document.visibilityState === "visible") refresh();
    };
    window.addEventListener("focus", onFocus);
    window.addEventListener("smrt-tasks-changed", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancelled = true;
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("smrt-tasks-changed", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  // Two-stage task pipeline:
  //   1. Fetch effect (this one) — pulls /smrt-tasks, classifies by title,
  //      extracts the project-ref / PO-number tokens out of each title and
  //      stashes the result in `rawChequeTasks`. No cheque lookup here so
  //      the effect doesn't need to depend on the cheques list.
  //   2. Resolve effect (below) — joins those raw tokens to the live
  //      cheques snapshot to produce the per-cheque status map the UI
  //      consumes. Splitting the two means the join re-runs whenever
  //      cheques change without retriggering an HTTP request.
  // Refetched on window focus so a user who marks a task complete in SMRT
  // Tasks and switches back sees the next action surface without a reload.
  // State declared here (not lower with the rest of the modal state) because
  // the resolve effect below references it synchronously in its deps array.
  const [rawChequeTasks, setRawChequeTasks] = useState<RawChequeTask[]>([]);
  useEffect(() => {
    let cancelled = false;
    const refetch = async () => {
      try {
        const res = await fetch(TASKS_API_URL, {
          credentials: "include",
          cache: "no-store",
        });
        if (!res.ok) return;
        const data = await res.json();
        const all: Array<{
          title?: string;
          description?: string;
          status?: string;
          assignedUserIds?: unknown;
        }> = [
          ...(Array.isArray(data?.myTasks) ? data.myTasks : []),
          ...(Array.isArray(data?.waiting) ? data.waiting : []),
          ...(Array.isArray(data?.completed) ? data.completed : []),
        ];
        const raw: RawChequeTask[] = [];
        for (const t of all) {
          const kind = classifyChequeTask(t.title);
          if (!kind || !t.status) continue;
          const refs = extractRefsFromTitle(t.title || "");
          const chequeId = extractChequeIdFromDescription(t.description);
          // Need at least one join key — chequeId for new CRMTIX tasks,
          // projectRef/poNumber for legacy tasks without the marker.
          if (!chequeId && !refs.projectRef && !refs.poNumber) continue;
          const assigneeIds = Array.isArray(t.assignedUserIds)
            ? (t.assignedUserIds.filter(
                (u) => typeof u === "string",
              ) as string[])
            : [];
          raw.push({
            kind,
            status: t.status,
            chequeId,
            projectRef: refs.projectRef,
            poNumber: refs.poNumber,
            assigneeIds,
          });
        }
        if (!cancelled) setRawChequeTasks(raw);
      } catch (err) {
        if (!cancelled) {
          console.error(
            "[SMRTCheques] failed to load cheque task statuses:",
            err,
          );
        }
      }
    };
    refetch();
    const onFocus = () => refetch();
    const onVisible = () => {
      if (document.visibilityState === "visible") refetch();
    };
    window.addEventListener("focus", onFocus);
    // Same-tab notification path: SMRTTasks dispatches this event after a
    // CRMTIX-source task completes so workflow gates (Add PO, etc.) update
    // without depending on focus changes or page remount.
    window.addEventListener("smrt-tasks-changed", onFocus);
    // Defensive: visibilitychange covers tab/window activation more reliably
    // than focus on some browsers.
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancelled = true;
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("smrt-tasks-changed", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  // Stage-2 resolve: join the raw classified tasks to the live cheques
  // snapshot. Project ref is the primary key (PO/Prepare/Send/Cashed all
  // carry it in their titles); PO number is the fallback for GR, whose
  // title is "GR Status for PO <num>" with no project ref. Multiple tasks
  // of the same kind per cheque shouldn't happen, but if they do Complete
  // wins so a stray rejection can't hide the next action.
  useEffect(() => {
    const map: Record<string, ChequeTaskStatus> = {};
    const assigneeMap: Record<
      string,
      Partial<Record<ChequeTaskKind, string[]>>
    > = {};
    for (const t of rawChequeTasks) {
      // Strict id-only join. Tasks without a chequeId marker (legacy
      // zombies from before the marker was added, OR orphaned tasks whose
      // cheques were deleted) are skipped. The projectRef fallback used
      // to live here, but it lets pre-existing completed tasks from prior
      // tests contaminate any new cheque that happens to reuse a
      // projectRef — and worse, the auto-advance effects then persist
      // that contamination back into the new cheque document. Strict id
      // match is the only way to keep test/recreate flows clean.
      if (!t.chequeId) continue;
      const cheque = cheques.find((c) => c.id === t.chequeId);
      if (!cheque) continue;
      const existing = map[cheque.id] || {};
      if (existing[t.kind] !== "Complete") {
        map[cheque.id] = { ...existing, [t.kind]: t.status };
      }
      // Union assignees across every matched task of this kind for this
      // cheque. Done unconditionally (not gated by status) so a user who
      // was assigned but where another task of the same kind already
      // resolved Complete still passes the gate.
      const kindAssignees = assigneeMap[cheque.id]?.[t.kind] || [];
      assigneeMap[cheque.id] = {
        ...(assigneeMap[cheque.id] || {}),
        [t.kind]: Array.from(
          new Set([...kindAssignees, ...t.assigneeIds]),
        ),
      };
    }
    setTaskStatusByCheque(map);
    setTaskAssigneesByCheque(assigneeMap);
  }, [rawChequeTasks, cheques]);

  // Esc closes any open modal. Single listener handles them all so the
  // dismiss behavior stays consistent.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setHistoryFor(null);
      setViewFor(null);
      setIssueFor(null);
      setUploadFor(null);
      setPendingDelete(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);
  const [view, setView] = useState<ViewMode>("tracker");
  const [draft, setDraft] = useState<ChequeRecord>(emptyDraft);
  const [extractionLoaded, setExtractionLoaded] = useState(false);
  // Most recent file selected from the upload zone. Setting this triggers
  // the extraction effect below; null means "no extraction in flight".
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  // Visible warning when PDF text extraction fails or yields nothing — so a
  // failed parse never silently masquerades as "the document had no data".
  const [extractWarning, setExtractWarning] = useState<string | null>(null);
  // Per-cheque, per-task-type status map. Populated by the resolve effect
  // (via rawChequeTasks); drives the Issue PO button (po === "Complete")
  // and the GR/sent auto-advance effects. Missing entry = task not found
  // (or fetch hasn't landed yet).
  const [taskStatusByCheque, setTaskStatusByCheque] = useState<
    Record<string, ChequeTaskStatus>
  >({});
  // Parallel map of task assignees per cheque/kind. Populated alongside
  // taskStatusByCheque from the same resolve effect. Action-button gates
  // grant access if the current user appears here for the relevant kind —
  // this is the source-of-truth path that survives drift between the
  // cheque's separately-stored `assignments` field and the live task.
  const [taskAssigneesByCheque, setTaskAssigneesByCheque] = useState<
    Record<string, Partial<Record<ChequeTaskKind, string[]>>>
  >({});
  const [pendingDelete, setPendingDelete] = useState<ChequeRecord | null>(null);
  // History modal state. When non-null, the workflow timeline is shown for
  // that cheque in a centered overlay (no separate page navigation).
  const [historyFor, setHistoryFor] = useState<ChequeRecord | null>(null);
  // Read-only data view modal state — same overlay pattern as history.
  const [viewFor, setViewFor] = useState<ChequeRecord | null>(null);
  // PDF generation: when set, the hidden QuoteLayout renders the data; an
  // effect then captures the node and triggers the download. Cleared on done.
  const [pendingQuote, setPendingQuote] = useState<QuoteDocData | null>(null);
  const quoteLayoutRef = useRef<HTMLDivElement>(null);

  // PDF capture pipeline: when pendingQuote is set, the hidden QuoteLayout
  // renders into the DOM. After React commits we hand the node off to
  // html2canvas → jsPDF and clear the pending state. Failure is logged but
  // doesn't break the cheque-creation flow that ran in parallel.
  useEffect(() => {
    if (!pendingQuote || !quoteLayoutRef.current) return;
    let cancelled = false;
    const node = quoteLayoutRef.current;
    const ref = pendingQuote.projectReference;
    // requestAnimationFrame ensures images (logo) have a chance to lay out
    // before the snapshot — without it, the first capture often misses them.
    const raf = requestAnimationFrame(() => {
      downloadQuotePdf(node, ref)
        .catch((err) =>
          console.error("[SMRTCheques] failed to generate PDF:", err),
        )
        .finally(() => {
          if (!cancelled) setPendingQuote(null);
        });
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
    };
  }, [pendingQuote]);
  // Issue-PO modal state. When non-null, the modal is open for that cheque.
  // Only the PO number is captured — the file-upload sub-field was removed
  // by request; PO documents now live wherever the org files them, not on
  // the cheque record.
  const [issueFor, setIssueFor] = useState<ChequeRecord | null>(null);
  const [issuePoNumber, setIssuePoNumber] = useState("");

  // Upload Cheque & Shipment modal state. Two optional photos — both stored
  // as base64 data URLs and persisted via the existing PUT /smrt-cheques/:id
  // path. Mirrors the issue-PO modal pattern.
  const [uploadFor, setUploadFor] = useState<ChequeRecord | null>(null);
  const [uploadChequeFileName, setUploadChequeFileName] = useState<string | undefined>(undefined);
  const [uploadChequeDataUrl, setUploadChequeDataUrl] = useState<string | undefined>(undefined);
  const [uploadUpsFileName, setUploadUpsFileName] = useState<string | undefined>(undefined);
  const [uploadUpsDataUrl, setUploadUpsDataUrl] = useState<string | undefined>(undefined);
  // In-flight flag for the Upload Cheque & Shipment modal's Submit button.
  // Photos are sent as base64 in a JSON body so the round-trip can be
  // multi-second on mobile — without this, users tap Submit again.
  const [submittingUpload, setSubmittingUpload] = useState(false);

  // Org users who hold SMRT Cheques module access (Owner role or any custom
  // role with the module's access key ticked). All RBAC + refetch logic
  // lives in the hook so future modules can reuse it with one call.
  const users: ModuleAccessUser[] = useUsersWithModuleAccess(SMRT_CHEQUES_ROUTE);

  const userName = useMemo(() => {
    const map: Record<string, string> = {};
    for (const u of users) map[u.id] = u.name;
    // Fall back to the raw id when an assignee no longer has access (e.g.
    // role removed after assignment) so the detail view doesn't go blank.
    return (id?: string) => (id ? map[id] || id : "—");
  }, [users]);

  // ── New-cheque builder handlers ─────────────────────────────────────────────
  // File-input setter only. Clears any prior extraction so a partial/failed
  // run can never leave stale fields visible, then stores the file — the
  // useEffect below picks it up and runs the extraction.
  const handleFile = (file: File | null) => {
    if (!file) return;
    const blank = emptyDraft();
    setExtractionLoaded(false);
    setExtractWarning(null);
    setDraft((prev) => ({
      ...prev,
      source: blank.source,
      quote: blank.quote,
    }));
    setSelectedFile(file);
  };

  // Runs extractFromFile whenever a new file is selected. Output is derived
  // entirely from the uploaded file (regex over the file's text + filename
  // fallback) — no caching, no defaults, no shared state across uploads.
  // The cancelled flag guards against a fast second upload completing first.
  useEffect(() => {
    if (!selectedFile) return;
    let cancelled = false;
    (async () => {
      const { source, warning } = await extractFromFile(selectedFile);
      if (cancelled) return;
      setDraft((prev) => ({
        ...prev,
        source,
        // Pre-fill the right-hand quote form from the extracted source so
        // the user is reviewing/adjusting, not re-typing.
        quote: {
          ...prev.quote,
          projectRef: source.projectRef,
          siteName: source.siteId,
          vendor: source.payable,
          scope: source.reasonForCheck,
          baseAmount: source.chequeAmount,
          total: computeTotal(
            source.chequeAmount,
            prev.quote.shippingFee,
            prev.quote.markupPct,
          ),
        },
      }));
      setExtractWarning(warning ?? null);
      setExtractionLoaded(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedFile]);

  const updateQuote = <K extends keyof ChequeRecord["quote"]>(
    key: K,
    value: ChequeRecord["quote"][K],
  ) => {
    setDraft((prev) => {
      const next = { ...prev.quote, [key]: value };
      next.total = computeTotal(next.baseAmount, next.shippingFee, next.markupPct);
      return { ...prev, quote: next };
    });
  };

  const updateAssignment = (step: keyof ChequeAssignments, userId: string) => {
    setDraft((prev) => ({
      ...prev,
      assignments: { ...prev.assignments, [step]: userId || undefined },
    }));
  };

  const generateQuote = async () => {
    // Stage the quote payload so the hidden <QuoteLayout> renders it in the
    // DOM, then capture that node with html2canvas → jsPDF. We do the capture
    // inside an effect (below) once React has flushed the render. Failures
    // are logged but don't block the cheque record from being persisted —
    // losing a download is recoverable, losing the record is not.
    setPendingQuote({
      clientName: draft.quote.vendor,
      siteName: draft.quote.siteName,
      projectReference: draft.quote.projectRef,
      scope: draft.quote.scope,
      baseAmount: draft.quote.baseAmount,
      shippingFee: draft.quote.shippingFee,
      markup:
        ((draft.quote.baseAmount + draft.quote.shippingFee) *
          draft.quote.markupPct) /
        100,
      total: draft.quote.total,
    });

    const payload: ChequeRecord = {
      ...draft,
      status: "quote_uploaded",
      timeline: { ...draft.timeline, quote_uploaded: new Date().toISOString() },
    };

    // Persist the cheque to the backend before doing anything else workflow-
    // related. Server returns the canonical doc (with its real id, orgID,
    // createdBy, createdAt) which we then use as the source of truth for
    // local state and the downstream task-creation chequeId reference. If the
    // POST fails we leave the user on the builder so they can retry — losing
    // the cheque silently is the worst possible outcome.
    let created: ChequeRecord;
    try {
      created = await createCheque(payload);
    } catch (err) {
      console.error("[SMRTCheques] failed to save cheque:", err);
      alert(
        "Couldn't save the cheque to the server. The quote PDF was generated " +
          "but the cheque wasn't persisted. Please try again.",
      );
      return;
    }

    setCheques((prev) => [created, ...prev]);
    setDraft(emptyDraft());
    setExtractionLoaded(false);
    setView("tracker");

    // Fire-and-forget: create the PO task in SMRT Tasks. Assignee is the
    // user the creator picked for the "Quote uploaded to Ericsson" step
    // (per spec — that user owns confirming the PO arrives). Match-back to
    // this cheque happens via the project ref in the title.
    const poAssignee =
      created.assignments.quote_uploaded || user?.userID;
    if (poAssignee) {
      createChequeTask({
        title: `PO Receipt for Cheque ${created.quote.projectRef}`,
        description:
          "Please confirm by marking this task as complete, that you have received the PO and then insert the PO number in the cheque tracker workflow",
        assigneeId: poAssignee,
        chequeId: created.id,
      });
    }
  };

  // ── Issue PO modal handlers ────────────────────────────────────────────────
  const openIssueModal = (c: ChequeRecord) => {
    setIssuePoNumber("");
    setIssueFor(c);
  };

  const submitIssue = async () => {
    if (!issueFor || !issuePoNumber.trim()) return;
    if (!issueFor.orgID) {
      console.error("[SMRTCheques] cheque has no orgID, cannot persist PO");
      alert("This cheque can't be updated — try refreshing the page.");
      return;
    }
    const id = issueFor.id;
    const nowIso = new Date().toISOString();
    const updated: ChequeRecord = {
      ...issueFor,
      status: "po_received",
      timeline: { ...issueFor.timeline, po_received: nowIso },
      po: { number: issuePoNumber.trim() },
    };

    let saved: ChequeRecord;
    try {
      saved = await updateCheque(updated, issueFor.orgID);
    } catch (err) {
      console.error("[SMRTCheques] failed to save PO:", err);
      alert("Couldn't save the PO. Please try again.");
      return;
    }
    setCheques((prev) => prev.map((c) => (c.id === id ? saved : c)));

    // Fire-and-forget: create the GR follow-up task in SMRT Tasks. Assignee
    // is the user the creator picked for the GR step in the workflow form.
    // Title carries the PO number (just submitted above) — that's how the
    // task-resolve effect joins it back to this cheque.
    const grAssignee = issueFor.assignments.gr_checked;
    const poNum = issuePoNumber.trim();
    if (grAssignee) {
      createChequeTask({
        title: `GR Status for PO ${poNum}`,
        description: `Please confirm by marking this task as complete, that payment for PO ${poNum} has been approved and the process to record in QBO completed`,
        assigneeId: grAssignee,
        chequeId: issueFor.id,
      });
    }

    setIssueFor(null);
  };

  const cancelDraft = () => {
    setDraft(emptyDraft());
    setExtractionLoaded(false);
    setSelectedFile(null);
    setExtractWarning(null);
    setView("tracker");
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    const id = pendingDelete.id;
    const orgID = pendingDelete.orgID;
    if (!orgID) {
      console.error("[SMRTCheques] cheque has no orgID, cannot delete");
      alert("This cheque can't be deleted — try refreshing the page.");
      return;
    }
    try {
      await deleteCheque(id, orgID);
    } catch (err) {
      console.error("[SMRTCheques] failed to delete cheque:", err);
      alert("Couldn't delete the cheque. Please try again.");
      return;
    }
    setCheques((prev) => prev.filter((c) => c.id !== id));
    // Close any open modal pinned to the cheque being deleted so the modal
    // body doesn't try to render against a now-missing record.
    setHistoryFor((cur) => (cur?.id === id ? null : cur));
    setViewFor((cur) => (cur?.id === id ? null : cur));
    setIssueFor((cur) => (cur?.id === id ? null : cur));
    setUploadFor((cur) => (cur?.id === id ? null : cur));
    setPendingDelete(null);
  };

  // ── Auto-advance moved to backend ──────────────────────────────────────────
  // GR / Sent / Cashed / Send-to-Client transitions used to live as useEffect
  // hooks in this file, which meant they only fired while the SMRTCheques
  // page was mounted. They now run server-side in
  // askSmart-backend/src/routes/smrtTasks.ts → runCrmtixAutoAdvance, dispatched
  // from PUT /smrt-tasks/:id/complete (and the cheque-side trigger for
  // Send-to-Client lives in PUT /smrt-cheques/:id). The user's tab no longer
  // needs to be on this page for the workflow to progress.

  // ── Upload Cheque & Shipment modal handlers ────────────────────────────────
  // Pre-fill from any previously saved shipment so a user who attached one
  // photo, hit Cancel/closed, and reopened doesn't have to re-attach it.
  // Step completion still requires BOTH photos to be present at submit time.
  const openUploadModal = (c: ChequeRecord) => {
    setUploadChequeFileName(c.shipment?.chequeFileName);
    setUploadChequeDataUrl(c.shipment?.chequeDataUrl);
    setUploadUpsFileName(c.shipment?.upsFileName);
    setUploadUpsDataUrl(c.shipment?.upsDataUrl);
    setUploadFor(c);
  };

  const handleUploadFile = (
    file: File | null,
    setName: (v: string | undefined) => void,
    setDataUrl: (v: string | undefined) => void,
  ) => {
    if (!file) {
      setName(undefined);
      setDataUrl(undefined);
      return;
    }

    // Direct iPhone camera captures land here as full-resolution JPEGs (often
    // 3–5 MB). Once base64-encoded they brush against the Cosmos 2 MB document
    // ceiling noted in smrtCheques.ts. Downscale + re-encode non-image files
    // are passed through untouched, and anything already small enough is also
    // passed through so existing desktop/gallery behaviour is preserved.
    const MAX_EDGE = 1600;
    const JPEG_QUALITY = 0.8;
    const PASSTHROUGH_BYTES = 1_000_000; // ~1 MB original → ~1.33 MB base64

    const readAsDataUrl = (blob: Blob, name: string) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result =
          typeof reader.result === "string" ? reader.result : undefined;
        console.log(
          "[SMRTCheques.upload] final",
          name,
          "b64Len",
          result?.length,
        );
        setName(name);
        setDataUrl(result);
      };
      reader.readAsDataURL(blob);
    };

    console.log(
      "[SMRTCheques.upload] picked",
      file.name,
      file.type,
      file.size,
    );

    if (!file.type.startsWith("image/") || file.size <= PASSTHROUGH_BYTES) {
      readAsDataUrl(file, file.name);
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      try {
        const scale = Math.min(
          1,
          MAX_EDGE / Math.max(img.width, img.height),
        );
        const w = Math.max(1, Math.round(img.width * scale));
        const h = Math.max(1, Math.round(img.height * scale));
        const canvas = document.createElement("canvas");
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          readAsDataUrl(file, file.name);
          return;
        }
        ctx.drawImage(img, 0, 0, w, h);
        const dataUrl = canvas.toDataURL("image/jpeg", JPEG_QUALITY);
        console.log(
          "[SMRTCheques.upload] compressed",
          file.name,
          `${img.width}x${img.height}->${w}x${h}`,
          "origBytes",
          file.size,
          "b64Len",
          dataUrl.length,
        );
        setName(file.name);
        setDataUrl(dataUrl);
      } catch (err) {
        console.error("[SMRTCheques.upload] compress failed:", err);
        readAsDataUrl(file, file.name);
      } finally {
        URL.revokeObjectURL(objectUrl);
      }
    };
    img.onerror = () => {
      console.warn(
        "[SMRTCheques.upload] decode failed, falling back to raw read",
        file.name,
      );
      URL.revokeObjectURL(objectUrl);
      readAsDataUrl(file, file.name);
    };
    img.src = objectUrl;
  };

  const submitUpload = async () => {
    if (!uploadFor) return;
    // Step is completed only when BOTH photos are present — that's the rule
    // for the cheque_prepared step. Submit is gated to require both, so this
    // guard is belt-and-braces.
    if (!uploadChequeDataUrl || !uploadUpsDataUrl) return;
    if (!uploadFor.orgID) {
      console.error("[SMRTCheques] cheque has no orgID, cannot persist shipment");
      alert("This cheque can't be updated — try refreshing the page.");
      return;
    }
    const id = uploadFor.id;
    const nowIso = new Date().toISOString();
    // Persist photos AND mark the cheque_prepared step complete. This step
    // is the one workflow advancement that's NOT task-driven — it advances
    // purely on UI submission. The downstream "Sent to Ericsson" step is
    // still task-driven (the Send-cheque task auto-advances on completion).
    const updated: ChequeRecord = {
      ...uploadFor,
      status: "cheque_prepared",
      timeline: { ...uploadFor.timeline, cheque_prepared: nowIso },
      shipment: {
        chequeFileName: uploadChequeFileName,
        chequeDataUrl: uploadChequeDataUrl,
        upsFileName: uploadUpsFileName,
        upsDataUrl: uploadUpsDataUrl,
      },
    };

    let saved: ChequeRecord;
    setSubmittingUpload(true);
    try {
      saved = await updateCheque(updated, uploadFor.orgID);
    } catch (err) {
      console.error("[SMRTCheques] failed to save shipment:", err);
      setSubmittingUpload(false);
      alert("Couldn't save the cheque shipment. Please try again.");
      return;
    }
    setSubmittingUpload(false);
    setCheques((prev) => prev.map((c) => (c.id === id ? saved : c)));

    // Send-to-Client task creation has moved to the backend. PUT
    // /smrt-cheques/:id will trigger maybeCreateSendToClientTask after this
    // save lands; that helper requires BOTH the photos to be uploaded
    // (cheque_prepared timeline, just stamped here) AND the Prepare Cheque
    // task to be Complete before it creates the follow-on task. The reverse
    // order (prepare task completing after photos are already uploaded) is
    // covered by runCrmtixAutoAdvance in smrtTasks.ts.
    //
    // Notify in-tab listeners so the rawChequeTasks fetch re-runs and any
    // newly-created Send-to-Client task surfaces immediately.
    window.dispatchEvent(new Event("smrt-tasks-changed"));

    setUploadFor(null);
  };

  // Workflow progression is owned by SMRT Tasks. This module is view-only —
  // it observes timeline/status updates that arrive from outside (e.g. a
  // task-completion hook) and renders them. No local mutation of step state.

  // ── Render branches ─────────────────────────────────────────────────────────
  const renderTracker = () => (
    <div className="smrtcheques-card">
      {cheques.length === 0 ? (
        <div className="smrtcheques-empty">
          No cheques yet. Click + New Cheque to start one.
        </div>
      ) : (
        <>
          <div className="smrtcheques-table-header">
            <div>Vendor</div>
            <div>Project</div>
            <div>Amount</div>
            <div>Status</div>
            <div>Last Updated</div>
            <div className="smrtcheques-table-header-actions">Actions</div>
          </div>
          {cheques.map((c) => {
            const pill = statusPill(c.status);
            const lastStep = getLastTimestamp(c.timeline);
            return (
              <div key={c.id} className="smrtcheques-table-row">
                <div className="smrtcheques-cell-strong" title={c.quote.vendor}>
                  {c.quote.vendor || "—"}
                </div>
                <div className="smrtcheques-cell" title={c.quote.projectRef}>
                  {c.quote.projectRef || "—"}
                </div>
                <div className="smrtcheques-cell">{fmtCurrency(c.quote.total)}</div>
                <div>
                  <span className={`smrtcheques-pill ${pill.className}`}>
                    {pill.label}
                  </span>
                </div>
                <div className="smrtcheques-cell">{fmtDateTime(lastStep)}</div>
                <div className="smrtcheques-cell smrtcheques-row-actions">
                  <button
                    type="button"
                    className="smrtcheques-btn-icon smrtcheques-btn-view"
                    title="View"
                    onClick={() => setViewFor(c)}
                  >
                    👁
                  </button>
                  <button
                    type="button"
                    className="smrtcheques-btn-icon smrtcheques-btn-history"
                    title="History"
                    onClick={() => setHistoryFor(c)}
                  >
                    ↻
                  </button>
                  {taskStatusByCheque[c.id]?.po === "Complete" &&
                    !c.timeline.po_received &&
                    (() => {
                      // Issue PO is the Quote Owner's action (their task is
                      // "PO Receipt for Cheque P-…"). Admins bypass. Allow
                      // either source: the cheque's recorded quote_uploaded
                      // assignee OR anyone the live SMRT Task was actually
                      // assigned to — the latter survives drift between the
                      // two stores.
                      const taskAssignees =
                        taskAssigneesByCheque[c.id]?.po || [];
                      const allowed =
                        isAdmin ||
                        (!!currentUserId &&
                          (currentUserId === c.assignments.quote_uploaded ||
                            taskAssignees.includes(currentUserId)));
                      const assigneeName = userName(
                        c.assignments.quote_uploaded,
                      );
                      return (
                        <button
                          type="button"
                          className="smrtcheques-btn-icon smrtcheques-btn-issue"
                          title={
                            allowed
                              ? "Issue PO"
                              : `Issue PO — assigned to ${assigneeName}`
                          }
                          disabled={!allowed}
                          onClick={() => openIssueModal(c)}
                        >
                          ✎
                        </button>
                      );
                    })()}
                  {/* Upload Cheque & Shipment — appears once GR is checked
                      (gr_checked timestamp set by the auto-advance effect)
                      and stays until BOTH photos have been submitted (which
                      is what marks cheque_prepared complete). Only the
                      Cheque Preparer (assignments.cheque_prepared) can
                      submit; admins bypass. */}
                  {c.timeline.gr_checked &&
                    !c.timeline.cheque_prepared &&
                    (() => {
                      // Admins bypass. Allow either source: the cheque's
                      // recorded cheque_prepared assignee OR anyone the live
                      // "Prepare Cheque" task is actually assigned to.
                      const taskAssignees =
                        taskAssigneesByCheque[c.id]?.prepare || [];
                      const allowed =
                        isAdmin ||
                        (!!currentUserId &&
                          (currentUserId === c.assignments.cheque_prepared ||
                            taskAssignees.includes(currentUserId)));
                      const assigneeName = userName(
                        c.assignments.cheque_prepared,
                      );
                      return (
                        <button
                          type="button"
                          className="smrtcheques-btn-icon smrtcheques-btn-issue"
                          title={
                            allowed
                              ? "Upload Cheque & Shipment"
                              : `Upload Cheque & Shipment — assigned to ${assigneeName}`
                          }
                          disabled={!allowed}
                          onClick={() => openUploadModal(c)}
                        >
                          📷
                        </button>
                      );
                    })()}
                  <button
                    type="button"
                    className="smrtcheques-btn-icon smrtcheques-btn-delete"
                    title="Delete cheque"
                    onClick={() => setPendingDelete(c)}
                  >
                    🗑
                  </button>
                </div>
              </div>
            );
          })}
        </>
      )}
    </div>
  );

  const renderBuilder = () => (
    <div className="smrtcheques-card">
      <div className="smrtcheques-toolbar">
        <button
          type="button"
          className="smrtcheques-btn-cancel"
          onClick={cancelDraft}
        >
          ← Back
        </button>
      </div>

      {extractWarning && (
        <div
          role="alert"
          style={{
            background: "rgba(212, 160, 23, 0.12)",
            border: "1px solid #d4a017",
            color: "#f5d77b",
            borderRadius: 6,
            padding: "10px 14px",
            marginBottom: 12,
            fontSize: "0.85rem",
            lineHeight: 1.4,
          }}
        >
          ⚠ {extractWarning}
        </div>
      )}

      {!extractionLoaded ? (
        <div className="smrtcheques-upload-zone">
          <div className="smrtcheques-upload-prompt">
            Upload the Ericsson cheque-request document
          </div>
          <label className="smrtcheques-upload-button">
            Choose File
            <input
              type="file"
              accept=".pdf,application/pdf"
              style={{ display: "none" }}
              onChange={(e) => {
                handleFile(e.target.files?.[0] || null);
                // Clear the input value so re-selecting the same file
                // afterwards still fires onChange (browser dedupes same-value
                // selections by default, which would silently swallow a
                // re-pick of the previously chosen document).
                e.target.value = "";
              }}
            />
          </label>
          <div className="smrtcheques-upload-hint">
            Fields will be extracted and shown side-by-side for review.
          </div>
        </div>
      ) : (
        <>
          <div className="smrtcheques-builder">
            {/* LEFT — extracted source data, read-only */}
            <div className="smrtcheques-builder-side">
              <div className="smrtcheques-builder-side-title">
                Extracted from Input Doc
              </div>
              {[
                ["Project Reference", draft.source.projectRef],
                ["Site ID", draft.source.siteId],
                ["Payable To", draft.source.payable],
                ["Cheque Amount", fmtCurrency(draft.source.chequeAmount)],
                ["Reason for Check", draft.source.reasonForCheck],
                ["Delivery Type", draft.source.deliveryType],
                ["Mail To", draft.source.mailTo],
                ["ATTN", draft.source.attn],
                ["Address", draft.source.address],
                ["City / State / Zip",
                  `${draft.source.city}, ${draft.source.state} ${draft.source.zip}`],
                ["Memo", draft.source.memo],
                ["Ericsson Contact", draft.source.ericssonContactEmail],
              ].map(([label, value]) => (
                <div key={label} className="smrtcheques-builder-readonly-row">
                  <div className="smrtcheques-builder-label">{label}</div>
                  <div className="smrtcheques-builder-value">{value || "—"}</div>
                </div>
              ))}
            </div>

            {/* RIGHT — editable quote form */}
            <div className="smrtcheques-builder-side">
              <div className="smrtcheques-builder-side-title">
                Quote Builder (editable)
              </div>

              <Field
                label="Project Reference"
                value={draft.quote.projectRef}
                onChange={(v) => updateQuote("projectRef", v)}
              />
              <Field
                label="Site Name"
                value={draft.quote.siteName}
                onChange={(v) => updateQuote("siteName", v)}
              />
              <Field
                label="Vendor / Payable To"
                value={draft.quote.vendor}
                onChange={(v) => updateQuote("vendor", v)}
              />
              <Field
                label="Scope"
                value={draft.quote.scope}
                onChange={(v) => updateQuote("scope", v)}
              />
              <NumberField
                label="Base Amount"
                value={draft.quote.baseAmount}
                onChange={(v) => updateQuote("baseAmount", v)}
              />
              <NumberField
                label="Shipping Fee"
                value={draft.quote.shippingFee}
                onChange={(v) => updateQuote("shippingFee", v)}
              />
              <NumberField
                label="Markup %"
                value={draft.quote.markupPct}
                onChange={(v) => updateQuote("markupPct", v)}
              />

              <div className="smrtcheques-summary">
                <div className="smrtcheques-summary-row">
                  <span>Base</span>
                  <span>{fmtCurrency(draft.quote.baseAmount)}</span>
                </div>
                <div className="smrtcheques-summary-row">
                  <span>Shipping</span>
                  <span>{fmtCurrency(draft.quote.shippingFee)}</span>
                </div>
                <div className="smrtcheques-summary-row">
                  <span>
                    Markup ({draft.quote.markupPct}% of base + shipping)
                  </span>
                  <span>
                    {fmtCurrency(
                      ((draft.quote.baseAmount + draft.quote.shippingFee) *
                        draft.quote.markupPct) /
                        100,
                    )}
                  </span>
                </div>
                <div className="smrtcheques-summary-row smrtcheques-summary-total">
                  <span>Total of PO</span>
                  <span>{fmtCurrency(draft.quote.total)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Workflow assignments — set up at quote generation, editable later */}
          <div className="smrtcheques-assignments">
            <div className="smrtcheques-builder-side-title">
              Assign Workflow Steps
            </div>
            <div className="smrtcheques-assignments-grid">
              {WORKFLOW.filter((s) => s.key !== "po_received").map((step) => (
                <div key={step.key} className="smrtcheques-assignment-row">
                  <div className="smrtcheques-builder-label">{step.label}</div>
                  <select
                    className="smrtcheques-input"
                    value={draft.assignments[step.key as keyof ChequeAssignments] || ""}
                    onChange={(e) =>
                      updateAssignment(
                        step.key as keyof ChequeAssignments,
                        e.target.value,
                      )
                    }
                  >
                    <option value="">
                      {users.length === 0
                        ? "— No users with SMRT CRMTIX access —"
                        : "— Select assignee —"}
                    </option>
                    {users.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.name}
                      </option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
          </div>

          <div className="smrtcheques-builder-actions">
            <button
              type="button"
              className="smrtcheques-btn-cancel"
              onClick={cancelDraft}
            >
              Cancel
            </button>
            <button
              type="button"
              className="smrtcheques-btn-primary"
              onClick={generateQuote}
              disabled={!isQuoteReady(draft)}
              title={
                isQuoteReady(draft)
                  ? "Generate the quote document"
                  : "Fill all quote fields and pick an assignee for every workflow step"
              }
            >
              Generate Quote
            </button>
          </div>
        </>
      )}
    </div>
  );

  // History modal — workflow timeline as a centered overlay. Read-only.
  // Dismissed via close button, overlay click, or Esc (handled by useEffect).
  const renderHistoryModal = () => {
    if (!historyFor) return null;
    return (
      <div
        className="smrtcheques-modal-overlay"
        onClick={() => setHistoryFor(null)}
      >
        <div
          className="smrtcheques-modal smrtcheques-modal-lg"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="smrtcheques-modal-header smrtcheques-modal-header-row">
            <div>
              <h2>History</h2>
              <div className="smrtcheques-detail-sub">
                {historyFor.quote.vendor} — {historyFor.quote.projectRef}
              </div>
            </div>
            <button
              type="button"
              className="smrtcheques-modal-close"
              title="Close"
              onClick={() => setHistoryFor(null)}
            >
              ×
            </button>
          </div>

          <div className="smrtcheques-modal-body">
            <div className="smrtcheques-workflow">
              {WORKFLOW.map((step) => {
                const completed = !!historyFor.timeline[step.key];
                // PO is now manually entered by the quote owner via the Issue
                // modal — assignee falls back to whoever uploaded the quote.
                const assigneeId =
                  step.key === "po_received"
                    ? historyFor.assignments.quote_uploaded
                    : historyFor.assignments[
                        step.key as keyof ChequeAssignments
                      ];
                const statusText = completed
                  ? fmtDateTime(historyFor.timeline[step.key])
                  : "Pending (task assigned)";

                return (
                  <div
                    key={step.key}
                    className={`smrtcheques-workflow-step${completed ? " smrtcheques-workflow-step-done" : ""}`}
                  >
                    <div className="smrtcheques-workflow-marker">
                      {completed ? "✓" : "•"}
                    </div>
                    <div className="smrtcheques-workflow-body">
                      <div className="smrtcheques-workflow-label">
                        {step.label}
                      </div>
                      <div className="smrtcheques-workflow-meta">
                        <span>
                          Completed by: <strong>{userName(assigneeId)}</strong>
                        </span>
                        <span className="smrtcheques-meta-sep">·</span>
                        <span>{statusText}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Read-only data view modal — original ingested values + final quote
  // totals. PO + Attachments are intentionally omitted at this stage.
  // Dismissed via close button, overlay click, or Esc (handled by useEffect).
  const renderDataViewModal = () => {
    if (!viewFor) return null;
    const q = viewFor.quote;
    const s = viewFor.source;
    const markupAmount =
      ((q.baseAmount + q.shippingFee) * q.markupPct) / 100;
    return (
      <div
        className="smrtcheques-modal-overlay"
        onClick={() => setViewFor(null)}
      >
        <div
          className="smrtcheques-modal smrtcheques-modal-lg"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="smrtcheques-modal-header smrtcheques-modal-header-row">
            <div>
              <h2>View</h2>
              <div className="smrtcheques-detail-sub">
                {q.vendor} — {q.projectRef}
              </div>
            </div>
            <button
              type="button"
              className="smrtcheques-modal-close"
              title="Close"
              onClick={() => setViewFor(null)}
            >
              ×
            </button>
          </div>

          <div className="smrtcheques-modal-body">
            {/* Original 2-column layout — Original Ericsson Data | Final
                Quote Data. Proof of Delivery + PO live INSIDE the right
                panel as a second totals-style box beneath the quote total. */}
            <div className="smrtcheques-builder">
              <div className="smrtcheques-builder-side">
                <div className="smrtcheques-builder-side-title">
                  Original Ericsson Data
                </div>
                {[
                  ["Project Reference", s.projectRef],
                  ["Site ID", s.siteId],
                  ["Payable To", s.payable],
                  ["Cheque Amount", fmtCurrency(s.chequeAmount)],
                  ["Reason for Check", s.reasonForCheck],
                  ["Delivery Type", s.deliveryType],
                  ["Mail To", s.mailTo],
                  ["ATTN", s.attn],
                  ["Address", s.address],
                  [
                    "City / State / Zip",
                    `${s.city}, ${s.state} ${s.zip}`,
                  ],
                  ["Memo", s.memo],
                  ["Ericsson Contact", s.ericssonContactEmail],
                ].map(([label, value]) => (
                  <div key={label} className="smrtcheques-builder-readonly-row">
                    <div className="smrtcheques-builder-label">{label}</div>
                    <div className="smrtcheques-builder-value">
                      {value || "—"}
                    </div>
                  </div>
                ))}
              </div>

              <div className="smrtcheques-builder-side">
                <div className="smrtcheques-builder-side-title">
                  Final Quote Data
                </div>
                {[
                  ["Project Reference", q.projectRef],
                  ["Site Name", q.siteName],
                  ["Vendor / Payable To", q.vendor],
                  ["Scope", q.scope],
                ].map(([label, value]) => (
                  <div key={label} className="smrtcheques-builder-readonly-row">
                    <div className="smrtcheques-builder-label">{label}</div>
                    <div className="smrtcheques-builder-value">
                      {value || "—"}
                    </div>
                  </div>
                ))}

                <div className="smrtcheques-summary">
                  <div className="smrtcheques-summary-row">
                    <span>Base</span>
                    <span>{fmtCurrency(q.baseAmount)}</span>
                  </div>
                  <div className="smrtcheques-summary-row">
                    <span>Shipping</span>
                    <span>{fmtCurrency(q.shippingFee)}</span>
                  </div>
                  <div className="smrtcheques-summary-row">
                    <span>Markup ({q.markupPct}% of base + shipping)</span>
                    <span>{fmtCurrency(markupAmount)}</span>
                  </div>
                  <div className="smrtcheques-summary-row smrtcheques-summary-total">
                    <span>Total of PO</span>
                    <span>{fmtCurrency(q.total)}</span>
                  </div>
                </div>

                {/* Proof of Delivery + PO — second totals-style box inside
                    the right panel, sitting directly under the quote total.
                    Same .smrtcheques-summary chrome so it visually matches.
                    Photo links open the data URL in a new tab where the
                    browser handles preview/download natively. */}
                <div className="smrtcheques-summary">
                  <div className="smrtcheques-summary-row">
                    <span>Cheque Image</span>
                    <span>
                      {viewFor.shipment?.chequeDataUrl ? (
                        <a
                          href={viewFor.shipment.chequeDataUrl}
                          target="_blank"
                          rel="noreferrer"
                          download={
                            viewFor.shipment.chequeFileName || "cheque.jpg"
                          }
                          style={{ color: "#1faa59" }}
                        >
                          {viewFor.shipment.chequeFileName || "View"}
                        </a>
                      ) : (
                        "—"
                      )}
                    </span>
                  </div>
                  <div className="smrtcheques-summary-row">
                    <span>UPS Envelope</span>
                    <span>
                      {viewFor.shipment?.upsDataUrl ? (
                        <a
                          href={viewFor.shipment.upsDataUrl}
                          target="_blank"
                          rel="noreferrer"
                          download={
                            viewFor.shipment.upsFileName || "ups-envelope.jpg"
                          }
                          style={{ color: "#1faa59" }}
                        >
                          {viewFor.shipment.upsFileName || "View"}
                        </a>
                      ) : (
                        "—"
                      )}
                    </span>
                  </div>
                  <div className="smrtcheques-summary-row smrtcheques-summary-total">
                    <span>PO Number</span>
                    <span>{viewFor.po?.number || "—"}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Workflow assignments — sits below the data panels. Static
                display of the initial assignees picked at quote setup;
                CRMTIX no longer edits these here. The History tab reflects
                the actual user who completed each step. Compact variant +
                max-height scroll keeps the modal within the viewport at
                high zoom. po_received is omitted (inbound from Ericsson,
                not internal). */}
            <div className="smrtcheques-assignments smrtcheques-assignments-compact">
              <div
                className="smrtcheques-builder-side-title"
                style={{ marginBottom: 12 }}
              >
                Workflow Assignments
              </div>

              <div className="smrtcheques-assignments-grid">
                {WORKFLOW.filter((s) => s.key !== "po_received").map(
                  (step, idx, arr) => {
                    const stepKey = step.key as keyof ChequeAssignments;
                    // Pair items across columns by setting BOTH row and
                    // column explicitly (gridColumn alone lets auto-flow
                    // push col-2 items into row 3+, breaking alignment):
                    //   row 1: Quote Uploaded   | Sent to Ericsson
                    //   row 2: GR Checked       | Cheque Cashed
                    //   row 3: Prepare Cheque   | (empty)
                    const half = Math.ceil(arr.length / 2);
                    const col = idx < half ? 1 : 2;
                    const row = (idx < half ? idx : idx - half) + 1;
                    return (
                      <div
                        key={step.key}
                        className="smrtcheques-assignment-row"
                        style={{ gridColumn: col, gridRow: row }}
                      >
                        <div className="smrtcheques-builder-label">
                          {step.label}
                        </div>
                        <div className="smrtcheques-builder-value">
                          {userName(viewFor.assignments[stepKey])}
                        </div>
                      </div>
                    );
                  },
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="smrtcheques-page">
      <h1 className="smrtcheques-title">SMRT CRMTIX</h1>

      <div className="smrtcheques-tabs">
        <div
          className={`smrtcheques-tab${view === "tracker" ? " smrtcheques-tab-active" : ""}`}
          onClick={() => setView("tracker")}
        >
          Cheques
          <span className="smrtcheques-tab-count">{cheques.length}</span>
        </div>
        <button
          type="button"
          className="smrtcheques-btn-primary"
          onClick={() => setView("new")}
        >
          + New Cheque
        </button>
      </div>

      {view === "tracker" && renderTracker()}
      {view === "new" && renderBuilder()}

      {renderHistoryModal()}
      {renderDataViewModal()}

      {issueFor && (
        <div
          className="smrtcheques-modal-overlay"
          onClick={() => setIssueFor(null)}
        >
          <div
            className="smrtcheques-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="smrtcheques-modal-header">
              <h2>Issue PO</h2>
            </div>
            <div className="smrtcheques-modal-body">
              <p className="smrtcheques-modal-text">
                Record the Ericsson PO for{" "}
                <strong>{issueFor.quote.vendor || "—"}</strong>
                {issueFor.quote.projectRef
                  ? ` (${issueFor.quote.projectRef})`
                  : ""}
                .
              </p>

              <div className="smrtcheques-builder-field">
                <div className="smrtcheques-builder-label">PO Number *</div>
                <input
                  className="smrtcheques-input"
                  value={issuePoNumber}
                  onChange={(e) => setIssuePoNumber(e.target.value)}
                  placeholder="e.g. 4500412345"
                />
              </div>

              <div className="smrtcheques-modal-actions">
                <button
                  type="button"
                  className="smrtcheques-btn-cancel"
                  onClick={() => setIssueFor(null)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="smrtcheques-btn-primary"
                  onClick={submitIssue}
                  disabled={!issuePoNumber.trim()}
                  title={
                    issuePoNumber.trim()
                      ? "Save PO and advance the cheque"
                      : "PO Number is required"
                  }
                >
                  Submit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {uploadFor && (
        <div
          className="smrtcheques-modal-overlay"
          onClick={() => setUploadFor(null)}
        >
          <div
            className="smrtcheques-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="smrtcheques-modal-header">
              <h2>Upload Cheque & Shipment</h2>
            </div>
            <div className="smrtcheques-modal-body">
              <p className="smrtcheques-modal-text">
                Attach the cheque image and UPS envelope photo for{" "}
                <strong>{uploadFor.quote.vendor || "—"}</strong>
                {uploadFor.quote.projectRef
                  ? ` (${uploadFor.quote.projectRef})`
                  : ""}
                .
              </p>

              <div className="smrtcheques-builder-field">
                <div className="smrtcheques-builder-label">Cheque photo</div>
                <input
                  type="file"
                  accept="image/*"
                  // capture="environment" makes mobile browsers (iOS Safari,
                  // Android Chrome) prefer the rear camera while still
                  // letting the user pick from the gallery. Desktop browsers
                  // ignore the attribute and fall back to the file picker.
                  capture="environment"
                  onChange={(e) =>
                    handleUploadFile(
                      e.target.files?.[0] || null,
                      setUploadChequeFileName,
                      setUploadChequeDataUrl,
                    )
                  }
                />
                {uploadChequeFileName && (
                  <div
                    style={{
                      marginTop: 4,
                      fontSize: "0.78rem",
                      color: "#9aa0a6",
                    }}
                  >
                    {uploadChequeFileName}
                  </div>
                )}
              </div>

              <div className="smrtcheques-builder-field">
                <div className="smrtcheques-builder-label">
                  UPS envelope photo
                </div>
                <input
                  type="file"
                  accept="image/*"
                  capture="environment"
                  onChange={(e) =>
                    handleUploadFile(
                      e.target.files?.[0] || null,
                      setUploadUpsFileName,
                      setUploadUpsDataUrl,
                    )
                  }
                />
                {uploadUpsFileName && (
                  <div
                    style={{
                      marginTop: 4,
                      fontSize: "0.78rem",
                      color: "#9aa0a6",
                    }}
                  >
                    {uploadUpsFileName}
                  </div>
                )}
              </div>

              <div className="smrtcheques-modal-actions">
                <button
                  type="button"
                  className="smrtcheques-btn-cancel"
                  onClick={() => setUploadFor(null)}
                  disabled={submittingUpload}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="smrtcheques-btn-primary"
                  onClick={submitUpload}
                  disabled={
                    submittingUpload ||
                    !uploadChequeDataUrl ||
                    !uploadUpsDataUrl
                  }
                  aria-busy={submittingUpload || undefined}
                  title={
                    submittingUpload
                      ? "Saving…"
                      : uploadChequeDataUrl && uploadUpsDataUrl
                        ? "Save photos and mark the prepare step complete"
                        : "Attach BOTH the cheque photo and the UPS envelope photo"
                  }
                >
                  {submittingUpload && (
                    <span
                      className="smrtcheques-btn-spinner"
                      aria-hidden
                      style={{ marginRight: 8 }}
                    />
                  )}
                  {submittingUpload ? "Saving…" : "Submit"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {pendingDelete && (
        <div
          className="smrtcheques-modal-overlay"
          onClick={() => setPendingDelete(null)}
        >
          <div
            className="smrtcheques-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="smrtcheques-modal-header">
              <h2>Delete Cheque</h2>
            </div>
            <div className="smrtcheques-modal-body">
              <p className="smrtcheques-modal-text">
                Delete cheque for{" "}
                <strong>{pendingDelete.quote.vendor || "—"}</strong>
                {pendingDelete.quote.projectRef
                  ? ` (${pendingDelete.quote.projectRef})`
                  : ""}
                ? This cannot be undone.
              </p>
              <div className="smrtcheques-modal-actions">
                <button
                  type="button"
                  className="smrtcheques-btn-cancel"
                  onClick={() => setPendingDelete(null)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="smrtcheques-btn-danger"
                  onClick={confirmDelete}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Hidden, off-screen quote layout. Rendered only while pendingQuote
          is set so html2canvas has a real DOM node to capture. position:fixed
          + left:-99999px keeps it out of the viewport without display:none
          (which would zero out its layout box). */}
      <div
        aria-hidden
        style={{
          position: "fixed",
          left: "-99999px",
          top: 0,
          pointerEvents: "none",
          zIndex: -1,
        }}
      >
        {pendingQuote && (
          <div ref={quoteLayoutRef}>
            <QuoteLayout data={pendingQuote} logoUrl={BROOK_LOGO_URL} />
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Inline subcomponents (kept local — page-only use) ────────────────────────

// Quote layout rendered into a hidden off-screen container, then snapshotted
// by html2canvas. Sized to QUOTE_PAGE_WIDTH_PX so the capture maps cleanly
// onto a portrait A4 page in jsPDF. All styles are inline + black-on-white
// because html2canvas inherits computed styles from the live DOM and we want
// a print-ready look regardless of the surrounding app theme.
function QuoteLayout({
  data,
  logoUrl,
}: {
  data: QuoteDocData;
  logoUrl: string;
}) {
  const fmt = (n: number) =>
    (Number.isFinite(n) ? n : 0).toLocaleString(undefined, {
      style: "currency",
      currency: "USD",
    });
  // A4 page height in pixels at 96 DPI. Each <PageWrapper> is locked to this
  // height so the existing canvas slicer in smrtChequesQuoteDoc.ts produces
  // exactly one PDF page per wrapper (no logic change required).
  const PAGE_H = QUOTE_PAGE_HEIGHT_PX;
  return (
    <div
      style={{
        width: `${QUOTE_PAGE_WIDTH_PX}px`,
        background: "#ffffff",
        color: "#000000",
        fontFamily: "Calibri, 'Carlito', Arial, sans-serif",
        fontSize: "11pt",
        lineHeight: 1.4,
      }}
    >
      {/* ── Page 1: cover ──────────────────────────────────────────────── */}
      <div
        style={{
          height: PAGE_H,
          padding: "48px 64px",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <PageHeader logoUrl={logoUrl} />
        <div
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
          }}
        >
          <div
            style={{
              fontSize: "40pt",
              fontWeight: 700,
              marginBottom: 32,
            }}
          >
            QUOTE
          </div>
          <div style={{ fontSize: "14pt" }}>{data.clientName || "—"}</div>
          <div style={{ fontSize: "14pt", marginTop: 4 }}>
            {data.siteName || "—"}
          </div>
          <div style={{ fontSize: "12pt", marginTop: 4 }}>
            Site Project {data.projectReference || "—"}
          </div>
        </div>
      </div>

      {/* ── Page 2: body ──────────────────────────────────────────────── */}
      <div
        style={{
          minHeight: PAGE_H,
          padding: "48px 64px",
          boxSizing: "border-box",
        }}
      >
        <PageHeader logoUrl={logoUrl} />

        <div style={{ marginTop: 28 }}>
          <Section n={1} title="Introduction">
            This quotation covers the services to be carried out by Brook
            Consultants Inc (&ldquo;
            <strong>Brook</strong>&rdquo;).
          </Section>

          <Section n={2} title="Scope &amp; Limitations">
            As per signed Master Services Agreement (&ldquo;
            <strong>MSA</strong>&rdquo;).
          </Section>

          <Section n={3} title="Resources &amp; Cost">
            <div style={{ fontStyle: "italic" }}>General</div>
            <div style={{ marginBottom: 12 }}>
              All Prices are quoted in US Dollars
            </div>

            <div style={{ fontStyle: "italic", marginTop: 8 }}>
              Scope and Cost
            </div>
            <table
              style={{
                borderCollapse: "collapse",
                marginTop: 4,
                fontSize: "11pt",
              }}
            >
              <tbody>
                <tr>
                  <td style={costLabelCell}>Scope:</td>
                  <td style={costValueCell}>{data.scope || "—"}</td>
                </tr>
                <tr>
                  <td style={costLabelCell}></td>
                  <td style={costValueCell}>
                    Payable to: {data.clientName || "—"}
                  </td>
                </tr>
                <tr>
                  <td style={costLabelCell}>Total of PO:</td>
                  <td style={costValueCell}>
                    <strong>{fmt(data.total)}</strong>
                  </td>
                </tr>
              </tbody>
            </table>
          </Section>

          <Section n={4} title="Terms of Payment">Standard.</Section>

          <Section
            n={5}
            title="Force Majeure / General Limitation of Liability"
          >
            This offer is subject to circumstances of force majeure and other
            circumstances outside our control. Either Party shall be excused
            from the performance or punctual performance of any of his
            obligations under this Contract and such obligations shall be
            extended by a period reasonable under the circumstances if the
            performance thereof is prevented or delayed by industrial
            disputes or any cause beyond the affected Party&apos;s
            reasonable control which, without in any way limiting the
            generality of the foregoing, shall include acts of God, riots,
            wars, accidents, embargo or requisition (acts of government),
            including non-availability of visas and permits for
            Contractor&apos;s personnel, or delays in the performance of its
            subcontractors caused by any such circumstances as referred to
            in this Article 5, Force Majeure. Nonavailability or withdrawal
            of any export/re-export licences shall for the purpose of this
            Article 5, Force Majeure, be considered as a cause beyond
            Contractor&apos;s reasonable control.
          </Section>

          <Section n={6} title="Terms and Conditions">As per MSA.</Section>

          <Section n={7} title="Ordering">
            <div>Please contact the undersigned for further information.</div>
            <div>Billy O&apos;Loughlin</div>
            <div>billy.oloughlin@brook.net</div>
            <div>+1 972 473 8918</div>
          </Section>
        </div>
      </div>
    </div>
  );
}

// A4 portrait page height in pixels at 96 DPI. Paired with QUOTE_PAGE_WIDTH_PX
// to give each <PageWrapper> the exact aspect ratio the slicer expects.
const QUOTE_PAGE_HEIGHT_PX = 1123;

// Repeats at the top of every PDF page — logo top-left, thin rule beneath.
function PageHeader({ logoUrl }: { logoUrl: string }) {
  return (
    <div
      style={{
        borderBottom: "1px solid #888",
        paddingBottom: 10,
      }}
    >
      <img
        src={logoUrl}
        alt="Brook"
        crossOrigin="anonymous"
        style={{ height: 48, objectFit: "contain", display: "block" }}
      />
    </div>
  );
}

// Cost rows in section 3. Label column is sized to the widest label so the
// values line up vertically (Scope: / Total of PO:).
const costLabelCell: React.CSSProperties = {
  paddingRight: 28,
  paddingTop: 2,
  paddingBottom: 2,
  verticalAlign: "top",
  whiteSpace: "nowrap",
};
const costValueCell: React.CSSProperties = {
  paddingTop: 2,
  paddingBottom: 2,
  verticalAlign: "top",
};

function Section({
  n,
  title,
  children,
}: {
  n: number;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div
        style={{
          fontWeight: 700,
          color: "#000000",
          marginBottom: 4,
        }}
      >
        {n}. {title}
      </div>
      {/* Body indented to align with the title text after "N. " — matches the
          hanging-indent style used in the reference document. */}
      <div style={{ marginLeft: 22 }}>{children}</div>
    </div>
  );
}

function Field(props: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="smrtcheques-builder-field">
      <div className="smrtcheques-builder-label">{props.label}</div>
      <input
        className="smrtcheques-input"
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
      />
    </div>
  );
}

function NumberField(props: {
  label: string;
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <div className="smrtcheques-builder-field">
      <div className="smrtcheques-builder-label">{props.label}</div>
      <input
        className="smrtcheques-input"
        type="number"
        step="0.01"
        value={Number.isFinite(props.value) ? props.value : 0}
        onChange={(e) => props.onChange(parseFloat(e.target.value) || 0)}
      />
    </div>
  );
}

function getLastTimestamp(t: ChequeTimeline): string | undefined {
  // Pick the most recent ISO string across all populated steps.
  const stamps = [
    t.cheque_cashed,
    t.cheque_sent,
    t.cheque_prepared,
    t.gr_checked,
    t.po_received,
    t.quote_uploaded,
  ].filter(Boolean) as string[];
  if (stamps.length === 0) return undefined;
  return stamps.sort().reverse()[0];
}
